/**
 * OrgHomeWidget — Home screen summary for org_pro tier.
 * Shows org name, member count, and quick-nav to org dashboard.
 */

import { useState, useEffect, useCallback } from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Building2, ChevronRight } from 'lucide-react-native';
import type { OrganizationMembership, Organization } from '@/types/database';

interface OrgSummary {
  orgName: string;
  memberCount: number;
  myRole: string;
}

export default function OrgHomeWidget() {
  const { theme } = useTheme();
  const { profile } = useAuth();
  const router = useRouter();
  const c = theme.colors;
  const [summary, setSummary] = useState<OrgSummary | null>(null);

  const load = useCallback(async () => {
    if (!profile?.id) return;
    try {
      // Find my active org membership
      const { data: memberships } = await supabase
        .from('organization_memberships')
        .select('*')
        .eq('status', 'active')
        .order('created_at', { ascending: true })
        .limit(1);

      const myMembership = (memberships as OrganizationMembership[] | null)?.[0];
      if (!myMembership) return;

      // Get org details
      const { data: org } = await supabase
        .from('organizations')
        .select('*')
        .eq('id', myMembership.org_id)
        .maybeSingle();

      if (!org) return;

      // Count members if admin/owner
      let memberCount = 0;
      if (myMembership.role === 'owner' || myMembership.role === 'admin') {
        const { count } = await supabase
          .from('organization_memberships')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', myMembership.org_id)
          .eq('status', 'active');
        memberCount = count ?? 0;
      }

      setSummary({
        orgName: (org as Organization).name,
        memberCount,
        myRole: myMembership.role,
      });
    } catch {
      setSummary(null);
    }
  }, [profile?.id]);

  useEffect(() => { load(); }, [load]);

  if (!summary) return null;

  return (
    <TouchableOpacity
      style={[styles.card, { backgroundColor: c.surface, borderColor: c.border }]}
      activeOpacity={0.7}
      onPress={() => router.push('/org-dashboard' as any)}
    >
      <View style={styles.header}>
        <Building2 size={18} color={c.accent} />
        <Text style={[styles.title, { color: c.text }]}>CLUB</Text>
        <View style={{ flex: 1 }} />
        <ChevronRight size={18} color={c.textTertiary} />
      </View>
      <Text style={[styles.orgName, { color: c.text }]}>{summary.orgName}</Text>
      <View style={styles.statsRow}>
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>{summary.memberCount}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Members</Text>
        </View>
        <View style={[styles.divider, { backgroundColor: c.border }]} />
        <View style={styles.stat}>
          <Text style={[styles.statValue, { color: c.text }]}>{summary.myRole.toUpperCase()}</Text>
          <Text style={[styles.statLabel, { color: c.textSecondary }]}>Your Role</Text>
        </View>
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 16,
    gap: 12,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    fontSize: 13,
    fontWeight: '700',
    letterSpacing: 0.5,
  },
  orgName: {
    fontSize: 18,
    fontWeight: '700',
  },
  statsRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  stat: {
    flex: 1,
    alignItems: 'center',
    gap: 2,
  },
  statValue: {
    fontSize: 20,
    fontWeight: '900',
  },
  statLabel: {
    fontSize: 12,
    fontWeight: '600',
  },
  divider: {
    width: 1,
    height: 36,
    marginHorizontal: 12,
  },
});
